import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  AlertTitle,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Stack,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  SwapHoriz,
  PersonAdd,
  Search,
  FilterList,
  Business,
  Refresh,
  TableChart,
  ViewColumn,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import crmService, {
  Lead,
  LeadStatus,
  LEAD_STATUSES,
  CrmSummary,
} from '../../services/crmService';

const statusTone: Record<LeadStatus, StatusTone> = {
  NEW: 'neutral',
  CONTACTED: 'info',
  QUALIFIED: 'warning',
  CONVERTED: 'success',
  LOST: 'error',
};

/** Theme-token colour per tone, for the plain swatch used inside menu items
 * (the menu's own text label already carries the word, so StatusDot's
 * built-in label would be redundant there). */
const toneSwatch: Record<StatusTone, string> = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  info: 'info.main',
  neutral: 'text.disabled',
};

/**
 * Statuses a user may set by hand.
 *
 * CONVERTED is deliberately absent: a lead becomes converted only through
 * POST /leads/:id/convert, which also creates the customer and opportunity in
 * one transaction. Letting someone just set the status would mark a lead
 * converted with nothing on the other side of it.
 *
 * Typed explicitly rather than relying on the predicate to narrow — filtering
 * a readonly tuple (LEAD_STATUSES is `as const`) does not reliably carry the
 * narrowed element type through, so the exclusion was being lost at the call
 * site and the full union reached handleChangeStatus.
 */
const CHANGEABLE_STATUSES: Array<Exclude<LeadStatus, 'CONVERTED'>> = LEAD_STATUSES.filter(
  (s): s is Exclude<LeadStatus, 'CONVERTED'> => s !== 'CONVERTED'
);

/** Statuses shown as Kanban columns — CONVERTED/LOST are terminal states,
 * looked at through the funnel/pipeline pages instead of a working board. */
const KANBAN_STATUSES: Array<'NEW' | 'CONTACTED' | 'QUALIFIED'> = ['NEW', 'CONTACTED', 'QUALIFIED'];

type LeadGrade = 'HOT' | 'WARM' | 'COLD';

/** Temperature -> status tone. HOT is urgent (error/red-orange), COLD is
 * deliberately neutral rather than "bad" — a cold lead isn't a failure state. */
const GRADE_TONE: Record<LeadGrade, StatusTone> = {
  HOT: 'error',
  WARM: 'warning',
  COLD: 'neutral',
};

/**
 * The lead-scoring fields (migration 059) aren't on the `Lead` interface yet,
 * so they're read defensively off the raw row rather than widening the
 * shared type here.
 */
interface LeadScoring {
  lead_score: number | null;
  lead_grade: LeadGrade | null;
  estimated_value: number | null;
  next_action: string | null;
  next_action_at: string | null;
}

const scoringOf = (lead: Lead): LeadScoring => {
  const raw = lead as unknown as Record<string, unknown>;
  const score = raw.lead_score;
  const value = raw.estimated_value;
  return {
    lead_score: score === null || score === undefined ? null : Number(score),
    lead_grade: (raw.lead_grade as LeadGrade | null | undefined) ?? null,
    estimated_value: value === null || value === undefined || value === '' ? null : Number(value),
    next_action: (raw.next_action as string | null | undefined) ?? null,
    next_action_at: (raw.next_action_at as string | null | undefined) ?? null,
  };
};

interface LeadFormData {
  companyName: string;
  leadSource: string;
  contactPerson: string;
  email: string;
  phone: string;
  status: Exclude<LeadStatus, 'CONVERTED'>;
  assignedTo: string;
  notes: string;
}

const emptyForm: LeadFormData = {
  companyName: '',
  leadSource: '',
  contactPerson: '',
  email: '',
  phone: '',
  status: 'NEW',
  assignedTo: '',
  notes: '',
};

interface ConvertFormData {
  createOpportunity: boolean;
  opportunityName: string;
  estimatedValue: string;
  probability: string;
  expectedClosureDate: string;
}

/** Score + temperature — a small bar for magnitude, StatusDot for the word.
 * Shared between the table cell and the Kanban card. */
const ScoreCell: React.FC<{ score: number | null; grade: LeadGrade | null }> = ({ score, grade }) => {
  if (score === null) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  const tone = grade ? GRADE_TONE[grade] : 'neutral';
  return (
    <Box sx={{ minWidth: 92 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {score}
        </Typography>
        {grade && <StatusDot label={grade} tone={tone} />}
      </Box>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover', overflow: 'hidden' }}>
        <Box
          sx={{
            width: `${Math.min(Math.max(score, 0), 100)}%`,
            height: '100%',
            bgcolor: toneSwatch[tone],
          }}
        />
      </Box>
    </Box>
  );
};

const NextActionCell: React.FC<{ text: string | null; at: string | null }> = ({ text, at }) => {
  if (!text) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ maxWidth: 200 }}>
      <Typography variant="body2" noWrap>
        {text}
      </Typography>
      {at && (
        <Typography variant="caption" color="text.secondary">
          {new Date(at).toLocaleDateString('en-IN')}
        </Typography>
      )}
    </Box>
  );
};

const LeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [summary, setSummary] = useState<CrmSummary | null>(null);

  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState<LeadGrade | ''>('');
  const [scoreSort, setScoreSort] = useState<'asc' | 'desc' | null>(null);

  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; lead: Lead } | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [form, setForm] = useState<LeadFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Lead | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [convertTarget, setConvertTarget] = useState<Lead | null>(null);
  const [convertForm, setConvertForm] = useState<ConvertFormData>({
    createOpportunity: true,
    opportunityName: '',
    estimatedValue: '',
    probability: '',
    expectedClosureDate: '',
  });
  const [converting, setConverting] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await crmService.getLeads({
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        search: search || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setLeads(result.leads);
      setTotal(result.pagination.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, search, page, rowsPerPage]);

  const fetchSummary = useCallback(async () => {
    try {
      setSummary(await crmService.getSummary());
    } catch {
      // Stat row simply stays hidden — never fake these numbers.
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Any filter change should reset back to page 1.
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sourceFilter, search]);

  // Grade + score sort apply client-side, on top of whatever page the server
  // returned — there's no grade param on the leads endpoint yet, so this
  // narrows/reorders the current page rather than fabricating a new fetch.
  const displayedLeads = useMemo(() => {
    let rows = leads;
    if (gradeFilter) {
      rows = rows.filter((l) => scoringOf(l).lead_grade === gradeFilter);
    }
    if (scoreSort) {
      rows = [...rows].sort((a, b) => {
        const sa = scoringOf(a).lead_score;
        const sb = scoringOf(b).lead_score;
        if (sa === null && sb === null) return 0;
        if (sa === null) return 1;
        if (sb === null) return -1;
        return scoreSort === 'asc' ? sa - sb : sb - sa;
      });
    }
    return rows;
  }, [leads, gradeFilter, scoreSort]);

  const kanbanColumns = useMemo(() => {
    const cols: Record<string, Lead[]> = {};
    KANBAN_STATUSES.forEach((s) => {
      cols[s] = displayedLeads.filter((l) => l.status === s);
    });
    return cols;
  }, [displayedLeads]);

  const openCreateDialog = () => {
    setEditingLead(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (lead: Lead) => {
    setEditingLead(lead);
    setForm({
      companyName: lead.company_name,
      leadSource: lead.lead_source || '',
      contactPerson: lead.contact_person || '',
      email: lead.email || '',
      phone: lead.phone || '',
      status: (lead.status === 'CONVERTED' ? 'NEW' : lead.status) as Exclude<LeadStatus, 'CONVERTED'>,
      assignedTo: lead.assigned_to ? String(lead.assigned_to) : '',
      notes: lead.notes || '',
    });
    setDialogOpen(true);
    setRowMenu(null);
  };

  const handleSaveLead = async () => {
    setSaving(true);
    try {
      const assignedTo = form.assignedTo.trim() ? parseInt(form.assignedTo, 10) : null;
      if (editingLead) {
        await crmService.updateLead(editingLead.id, {
          companyName: form.companyName,
          leadSource: form.leadSource || null,
          contactPerson: form.contactPerson || null,
          email: form.email || null,
          phone: form.phone || null,
          assignedTo,
          notes: form.notes || null,
        });
        setToast({ message: 'Lead updated', severity: 'success' });
      } else {
        await crmService.createLead({
          companyName: form.companyName,
          leadSource: form.leadSource || null,
          contactPerson: form.contactPerson || null,
          email: form.email || null,
          phone: form.phone || null,
          status: form.status,
          assignedTo,
          notes: form.notes || null,
        });
        setToast({ message: 'Lead created', severity: 'success' });
      }
      setDialogOpen(false);
      fetchLeads();
      fetchSummary();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to save lead', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangeStatus = async (lead: Lead, status: Exclude<LeadStatus, 'CONVERTED'>) => {
    setRowMenu(null);
    try {
      await crmService.updateLeadStatus(lead.id, status);
      setToast({ message: `Status changed to ${status}`, severity: 'success' });
      fetchLeads();
      fetchSummary();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to update status', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await crmService.deleteLead(deleteTarget.id);
      setToast({ message: 'Lead deleted', severity: 'success' });
      setDeleteTarget(null);
      fetchLeads();
      fetchSummary();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to delete lead', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const openConvertDialog = (lead: Lead) => {
    setRowMenu(null);
    setConvertTarget(lead);
    setConvertForm({
      createOpportunity: true,
      opportunityName: `${lead.company_name} opportunity`,
      estimatedValue: '',
      probability: '',
      expectedClosureDate: '',
    });
  };

  const handleConvert = async () => {
    if (!convertTarget) return;
    setConverting(true);
    try {
      const result = await crmService.convertLead(convertTarget.id, {
        createOpportunity: convertForm.createOpportunity,
        opportunityName: convertForm.createOpportunity ? convertForm.opportunityName : undefined,
        estimatedValue: convertForm.createOpportunity && convertForm.estimatedValue
          ? parseFloat(convertForm.estimatedValue)
          : undefined,
        probability: convertForm.createOpportunity && convertForm.probability
          ? parseFloat(convertForm.probability)
          : undefined,
        expectedClosureDate: convertForm.createOpportunity && convertForm.expectedClosureDate
          ? convertForm.expectedClosureDate
          : undefined,
      });
      setToast({
        message: `Converted — customer ${result.customer.customer_code} created${
          result.opportunity ? ' with a new opportunity' : ''
        }`,
        severity: 'success',
      });
      setConvertTarget(null);
      fetchLeads();
      fetchSummary();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to convert lead', severity: 'error' });
    } finally {
      setConverting(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setSourceFilter('');
    setSearch('');
    setGradeFilter('');
    setScoreSort(null);
  };

  const conversionRateLabel = useMemo(() => {
    if (!summary || summary.conversionRatePct === null) return null;
    return `${summary.conversionRatePct}%`;
  }, [summary]);

  const toggleScoreSort = () => {
    setScoreSort((prev) => (prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc'));
  };

  return (
    <Box>
      <PageHeader
        title="Leads"
        subtitle="Prospects moving toward a customer record"
        actions={
          <>
            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
            >
              <ToggleButton value="table" aria-label="Table view">
                <TableChart fontSize="small" />
              </ToggleButton>
              <ToggleButton value="kanban" aria-label="Kanban view">
                <ViewColumn fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            <IconButton onClick={fetchLeads} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={openCreateDialog}>
              New Lead
            </Button>
          </>
        }
      />

      {summary && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 2.5,
          }}
        >
          <StatCard title="New This Week" value={summary.newLeadsThisWeek} icon={<PersonAdd />} color="#0284c7" />
          <StatCard title="Qualified" value={summary.qualifiedLeads} icon={<Business />} color="#059669" />
          <StatCard title="Conversion Rate" value={conversionRateLabel} icon={<SwapHoriz />} color="#7c3aed" />
        </Box>
      )}

      <FilterBar>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as LeadStatus | '')}
          sx={{ minWidth: 180 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {LEAD_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Grade"
          value={gradeFilter}
          onChange={(e) => setGradeFilter(e.target.value as LeadGrade | '')}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All Grades</MenuItem>
          <MenuItem value="HOT">Hot</MenuItem>
          <MenuItem value="WARM">Warm</MenuItem>
          <MenuItem value="COLD">Cold</MenuItem>
        </TextField>

        <TextField
          size="small"
          label="Source"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        />

        <TextField
          size="small"
          label="Search company, contact, email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <FilterSpacer />

        {(statusFilter || sourceFilter || search || gradeFilter || scoreSort) && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {viewMode === 'table' ? (
        <TableSurface>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Company</TableCell>
                  <TableCell sx={headerCellSx}>Contact</TableCell>
                  <TableCell sx={headerCellSx}>Email</TableCell>
                  <TableCell sx={headerCellSx}>Phone</TableCell>
                  <TableCell sx={headerCellSx}>Source</TableCell>
                  <TableCell sx={headerCellSx}>
                    <TableSortLabel
                      active={scoreSort !== null}
                      direction={scoreSort ?? 'desc'}
                      onClick={toggleScoreSort}
                    >
                      Score
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Est. Value
                  </TableCell>
                  <TableCell sx={headerCellSx}>Next Action</TableCell>
                  <TableCell sx={headerCellSx}>Assigned To</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={headerCellSx}>Created</TableCell>
                  <TableCell sx={headerCellSx} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 6, border: 0 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : displayedLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} sx={{ border: 0 }}>
                      <TableEmptyState
                        title="No leads match these filters"
                        hint="Clear the filters, or create a new lead."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedLeads.map((lead) => {
                    const s = scoringOf(lead);
                    return (
                      <TableRow key={lead.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {lead.company_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{lead.contact_person || '—'}</TableCell>
                        <TableCell>{lead.email || '—'}</TableCell>
                        <TableCell>{lead.phone || '—'}</TableCell>
                        <TableCell>{lead.lead_source || '—'}</TableCell>
                        <TableCell>
                          <ScoreCell score={s.lead_score} grade={s.lead_grade} />
                        </TableCell>
                        <TableCell align="right">
                          {s.estimated_value !== null ? formatIndianNumber(s.estimated_value) : '—'}
                        </TableCell>
                        <TableCell>
                          <NextActionCell text={s.next_action} at={s.next_action_at} />
                        </TableCell>
                        <TableCell>{lead.assigned_to_name || '—'}</TableCell>
                        <TableCell>
                          <StatusDot label={lead.status} tone={statusTone[lead.status]} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(lead.created_at).toLocaleDateString('en-IN')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            aria-label={`Actions for ${lead.company_name}`}
                            onClick={(e) => setRowMenu({ anchor: e.currentTarget, lead })}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </TableSurface>
      ) : (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Showing New, Contacted and Qualified from the current page — Converted and Lost leads stay in Table view.
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 2,
                alignItems: 'start',
              }}
            >
              {KANBAN_STATUSES.map((status) => {
                const rows = kanbanColumns[status] || [];
                return (
                  <Paper key={status} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 0.5 }}>
                      <StatusDot label={status} tone={statusTone[status]} />
                      <Typography variant="caption" color="text.secondary">
                        ({rows.length})
                      </Typography>
                    </Box>
                    <Stack spacing={1.25}>
                      {rows.length === 0 ? (
                        <Typography variant="caption" color="text.disabled" sx={{ px: 0.5, fontStyle: 'italic' }}>
                          No leads
                        </Typography>
                      ) : (
                        rows.map((lead) => {
                          const s = scoringOf(lead);
                          return (
                            <Paper
                              key={lead.id}
                              elevation={0}
                              onClick={() => openEditDialog(lead)}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                cursor: 'pointer',
                                transition: 'border-color .15s',
                                '&:hover': { borderColor: 'primary.main' },
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {lead.company_name}
                              </Typography>
                              <Box sx={{ mt: 0.75 }}>
                                <ScoreCell score={s.lead_score} grade={s.lead_grade} />
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 110 }}>
                                  {lead.assigned_to_name || '—'}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  {s.estimated_value !== null ? formatIndianNumber(s.estimated_value) : '—'}
                                </Typography>
                              </Box>
                              {s.next_action && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} noWrap>
                                  Next: {s.next_action}
                                </Typography>
                              )}
                            </Paper>
                          );
                        })
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* Row action menu */}
      <Menu anchorEl={rowMenu?.anchor || null} open={!!rowMenu} onClose={() => setRowMenu(null)}>
        <MenuItem
          onClick={() => rowMenu && openEditDialog(rowMenu.lead)}
          disabled={rowMenu?.lead.status === 'CONVERTED'}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <Divider />
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
          Change status
        </Typography>
        {CHANGEABLE_STATUSES.map((s) => (
          <MenuItem
            key={s}
            onClick={() => rowMenu && handleChangeStatus(rowMenu.lead, s)}
            disabled={rowMenu?.lead.status === 'CONVERTED' || rowMenu?.lead.status === s}
          >
            <ListItemIcon>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: toneSwatch[statusTone[s]] }} />
            </ListItemIcon>
            <ListItemText>{s}</ListItemText>
          </MenuItem>
        ))}

        <Divider />
        <MenuItem
          onClick={() => rowMenu && openConvertDialog(rowMenu.lead)}
          disabled={rowMenu?.lead.status === 'CONVERTED'}
        >
          <ListItemIcon>
            <SwapHoriz fontSize="small" />
          </ListItemIcon>
          <ListItemText>Convert…</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenu) setDeleteTarget(rowMenu.lead);
            setRowMenu(null);
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingLead ? 'Edit Lead' : 'New Lead'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Company Name"
              value={form.companyName}
              onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Contact Person"
                value={form.contactPerson}
                onChange={(e) => setForm((f) => ({ ...f, contactPerson: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Lead Source"
                value={form.leadSource}
                onChange={(e) => setForm((f) => ({ ...f, leadSource: e.target.value }))}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Phone"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              />
            </Box>
            {!editingLead && (
              <TextField
                select
                fullWidth
                label="Initial Status"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as Exclude<LeadStatus, 'CONVERTED'> }))}
              >
                {CHANGEABLE_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              fullWidth
              label="Assigned To (User ID)"
              value={form.assignedTo}
              onChange={(e) => setForm((f) => ({ ...f, assignedTo: e.target.value.replace(/[^0-9]/g, '') }))}
              helperText="Optional — numeric user ID"
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveLead} disabled={saving || !form.companyName.trim()}>
            {saving ? 'Saving…' : editingLead ? 'Save Changes' : 'Create Lead'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Lead</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.company_name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert confirmation — explains exactly what gets created; irreversible. */}
      <Dialog open={!!convertTarget} onClose={() => setConvertTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Convert Lead</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            <AlertTitle>This cannot be undone</AlertTitle>
            Converting "{convertTarget?.company_name}" will:
            <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
              <li>Create a new customer record from this lead's details</li>
              <li>Mark this lead as CONVERTED and lock it from further edits</li>
              <li>Optionally create a new sales opportunity for that customer</li>
            </Box>
          </Alert>

          <FormControlLabel
            control={
              <Checkbox
                checked={convertForm.createOpportunity}
                onChange={(e) => setConvertForm((f) => ({ ...f, createOpportunity: e.target.checked }))}
              />
            }
            label="Also create an opportunity"
          />

          {convertForm.createOpportunity && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Opportunity Name"
                value={convertForm.opportunityName}
                onChange={(e) => setConvertForm((f) => ({ ...f, opportunityName: e.target.value }))}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Estimated Value"
                  type="number"
                  value={convertForm.estimatedValue}
                  onChange={(e) => setConvertForm((f) => ({ ...f, estimatedValue: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Probability (%)"
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  value={convertForm.probability}
                  onChange={(e) => setConvertForm((f) => ({ ...f, probability: e.target.value }))}
                />
              </Box>
              <TextField
                fullWidth
                size="small"
                label="Expected Close Date"
                type="date"
                value={convertForm.expectedClosureDate}
                onChange={(e) => setConvertForm((f) => ({ ...f, expectedClosureDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setConvertTarget(null)} disabled={converting}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={handleConvert} disabled={converting}>
            {converting ? 'Converting…' : 'Convert Lead'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default LeadsPage;
